import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { UserPlus, LogIn, Sparkles } from 'lucide-react';

const AuthModal = ({ isOpen, onClose }) => {
  const { login, register, userPresets } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  const [loginData, setLoginData] = useState({
    email: 'demo@crewmate.com',
    password: 'demo123'
  });

  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    crewName: '',
    preset: 'intermediate',
    honeypot: '', // Champ honeypot pour détecter les bots
    captcha: '' // CAPTCHA simple
  });

  // Génération d'un CAPTCHA simple
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [formStartTime, setFormStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Fonction pour évaluer la force du mot de passe
  const getPasswordStrength = (password) => {
    if (password.length < 8) return { strength: 'weak', color: 'red', text: 'Too short' };
    if (!/(?=.*[a-z])/.test(password)) return { strength: 'weak', color: 'red', text: 'Need lowercase' };
    if (!/(?=.*[A-Z])/.test(password)) return { strength: 'weak', color: 'red', text: 'Need uppercase' };
    if (!/(?=.*\d)/.test(password)) return { strength: 'weak', color: 'red', text: 'Need number' };
    if (password.length >= 12 && /(?=.*[!@#$%^&*])/.test(password)) return { strength: 'strong', color: 'green', text: 'Strong' };
    return { strength: 'medium', color: 'orange', text: 'Good' };
  };

  // Ajout d'un hook pour détecter le thème
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.body.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.classList.contains('light') ? 'light' : 'dark');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Génération du CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const question = `${num1} + ${num2} = ?`;
    const answer = num1 + num2;
    setCaptchaQuestion(question);
    setCaptchaAnswer(answer.toString());
  };

  // Initialiser le CAPTCHA et le timer quand l'onglet register est ouvert
  useEffect(() => {
    if (activeTab === 'register') {
      generateCaptcha();
      setFormStartTime(Date.now());
      setTimeRemaining(5);
    }
  }, [activeTab]);

  // Timer pour le délai de sécurité
  useEffect(() => {
    if (activeTab === 'register' && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeRemaining, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast({
        title: "Welcome back! 👋",
        description: "Successfully logged into your crewmate account."
      });
      onClose();
    } else {
      toast({
        title: "Login Failed",
        description: result.error,
        variant: "destructive"
      });
    }
    
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Vérification du délai de sécurité (minimum 5 secondes)
    if (formStartTime && Date.now() - formStartTime < 5000) {
      toast({
        title: "Please Wait",
        description: "Please take your time to fill out the form properly.",
        variant: "destructive"
      });
      setIsLoading(false);
      return;
    }

    // Vérification du champ honeypot (doit rester vide)
    if (registerData.honeypot) {
      console.log('Bot détecté via honeypot');
      toast({
        title: "Registration Blocked",
        description: "Suspicious activity detected.",
        variant: "destructive"
      });
      setIsLoading(false);
      return;
    }

    // Vérification du CAPTCHA
    if (registerData.captcha !== captchaAnswer) {
      toast({
        title: "CAPTCHA Failed",
        description: "Please solve the math problem correctly.",
        variant: "destructive"
      });
      generateCaptcha(); // Générer un nouveau CAPTCHA
      setIsLoading(false);
      return;
    }

    // Validation du nom d'utilisateur
    if (!registerData.username || registerData.username.length < 3) {
      toast({
        title: "Invalid Username",
        description: "Username must be at least 3 characters long.",
        variant: "destructive"
      });
      setIsLoading(false);
      return;
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      setIsLoading(false);
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match.",
        variant: "destructive"
      });
      setIsLoading(false);
      return;
    }

    if (registerData.password.length < 8) {
      toast({
        title: "Weak Password",
        description: "Password must be at least 8 characters long.",
        variant: "destructive"
      });
      setIsLoading(false);
      return;
    }

    // Vérification de la complexité du mot de passe
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(registerData.password)) {
      toast({
        title: "Weak Password",
        description: "Password must contain at least one uppercase letter, one lowercase letter, and one number.",
        variant: "destructive"
      });
      setIsLoading(false);
      return;
    }

    const result = await register(registerData);
    
    if (result.success) {
      toast({
        title: "Welcome to crewmate! 🎉",
        description: "Your account has been created successfully."
      });
      onClose();
    } else {
      toast({
        title: "Registration Failed",
        description: result.error,
        variant: "destructive"
      });
    }
    
    setIsLoading(false);
  };

  const handleLoginInputChange = (field, value) => {
    setLoginData(prev => ({ ...prev, [field]: value }));
  };

  const handleRegisterInputChange = (field, value) => {
    setRegisterData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-md max-h-[90vh] overflow-y-auto border-2 bg-white text-black`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Join crewmate
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="login" className="flex items-center gap-2">
              <LogIn className="w-4 h-4" />
              Login
            </TabsTrigger>
            <TabsTrigger value="register" className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-4 pb-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => handleLoginInputChange('email', e.target.value)}
                  placeholder="your@email.com"
                  required
                  className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => handleLoginInputChange('password', e.target.value)}
                  placeholder="Your password"
                  required
                  className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Demo Account</h4>
              <p className="text-sm text-blue-700">
                Use demo@crewmate.com / demo123 to try the app
              </p>
            </div>
          </TabsContent>

          <TabsContent value="register" className="space-y-4 pb-4">
            <form onSubmit={handleRegister} className="space-y-4">
              {/* Champ honeypot caché pour détecter les bots */}
              <input
                type="text"
                name="honeypot"
                value={registerData.honeypot}
                onChange={(e) => handleRegisterInputChange('honeypot', e.target.value)}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="register-username">Username *</Label>
                  <Input
                    id="register-username"
                    value={registerData.username}
                    onChange={(e) => handleRegisterInputChange('username', e.target.value)}
                    placeholder="bboy_username"
                    required
                    className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email">Email *</Label>
                  <Input
                    id="register-email"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => handleRegisterInputChange('email', e.target.value)}
                    placeholder="your@email.com"
                    required
                    className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-display-name">Display Name</Label>
                <Input
                  id="register-display-name"
                  value={registerData.displayName}
                  onChange={(e) => handleRegisterInputChange('displayName', e.target.value)}
                  placeholder="Your display name"
                    className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-crew">Crew Name (Optional)</Label>
                <Input
                  id="register-crew"
                  value={registerData.crewName}
                  onChange={(e) => handleRegisterInputChange('crewName', e.target.value)}
                  placeholder="Your crew name"
                  className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="register-password">Password *</Label>
                  <Input
                    id="register-password"
                    type="password"
                    value={registerData.password}
                    onChange={(e) => handleRegisterInputChange('password', e.target.value)}
                    placeholder="Min 8 characters with uppercase, lowercase, and number"
                    required
                    className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                  />
                  {registerData.password && (
                    <div className="flex items-center gap-2 text-xs">
                      <span>Strength:</span>
                      <span 
                        className={`px-2 py-1 rounded text-white ${
                          getPasswordStrength(registerData.password).color === 'red' ? 'bg-red-500' :
                          getPasswordStrength(registerData.password).color === 'orange' ? 'bg-orange-500' :
                          'bg-green-500'
                        }`}
                      >
                        {getPasswordStrength(registerData.password).text}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-confirm">Confirm Password *</Label>
                  <Input
                    id="register-confirm"
                    type="password"
                    value={registerData.confirmPassword}
                    onChange={(e) => handleRegisterInputChange('confirmPassword', e.target.value)}
                    placeholder="Confirm password"
                    required
                    className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Choose Your Starting Preset</Label>
                <div className="grid gap-3">
                  {Object.entries(userPresets).map(([key, preset]) => (
                    <Card 
                      key={key}
                      className={`cursor-pointer transition-all duration-200 ${theme === 'light' ? 'bg-white border-black text-black' : ''} ${registerData.preset === key ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => handleRegisterInputChange('preset', key)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm">{preset.name}</CardTitle>
                          {registerData.preset === key && (
                            <Badge className={`bg-blue-600 ${theme === 'light' ? 'text-white' : ''}`}>Selected</Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <CardDescription className="text-xs">{preset.description}</CardDescription>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {Object.keys(preset.variations).slice(0, 3).map(category => (
                            <Badge key={category} variant="outline" className={`text-xs ${theme === 'light' ? 'bg-white text-black border-black' : ''}`}>
                              {category}
                            </Badge>
                          ))}
                          {Object.keys(preset.variations).length > 3 && (
                            <Badge variant="outline" className={`text-xs ${theme === 'light' ? 'bg-white text-black border-black' : ''}`}>
                              +{Object.keys(preset.variations).length - 3} more
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* CAPTCHA */}
              <div className="space-y-2">
                <Label htmlFor="register-captcha">Security Check *</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Input
                      id="register-captcha"
                      value={registerData.captcha}
                      onChange={(e) => handleRegisterInputChange('captcha', e.target.value)}
                      placeholder="Answer"
                      required
                      className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
                    />
                  </div>
                  <div className="text-sm font-mono bg-gray-100 px-3 py-2 rounded border">
                    {captchaQuestion}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateCaptcha}
                    className="text-xs"
                  >
                    🔄
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Please solve the math problem to verify you're human
                </p>
              </div>

              <Button
                type="submit"
                disabled={isLoading || timeRemaining > 0}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Account...' : 
                 timeRemaining > 0 ? `Please wait ${timeRemaining}s...` : 
                 'Create Account'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;